import React, { useContext, useCallback, useState, useRef } from 'react'
import { useField } from 'formik'

import { XStack, XStackProps } from 'tamagui'

import { Icon } from '@newComponents/Icon'

import { FormFieldContext } from './shared'

import './FormStepper.css'

export type StepperProps = {
  value: number
  onChange: (value: number) => void
  onIncrement?: () => void
  onDecrement?: () => void
  min?: number
  max?: number
  step?: number
  incrementDisabled?: boolean
  decrementDisabled?: boolean
  displayValue?: string
  editable?: boolean
} & Omit<XStackProps, 'children'>

const StepperButton = ({
  onPress,
  disabled,
  icon,
}: {
  onPress: () => void
  disabled?: boolean
  icon: 'add' | 'subtract'
}) => (
  <button
    type='button'
    className='FormStepper_button'
    onClick={onPress}
    disabled={disabled}
    aria-label={icon === 'add' ? 'Increase' : 'Decrease'}
  >
    <Icon name={icon} size='$4' color='$color' />
  </button>
)

// Always renders a single <input> element to prevent layout shift when toggling edit mode.
// In display mode the input is readOnly + unstyled; on focus it becomes editable with a focus ring.

const StepperValue = ({
  value,
  displayValue,
  editable,
  onChange,
  min,
  max,
}: {
  value: number
  displayValue?: string
  editable: boolean
  onChange: (value: number) => void
  min: number
  max: number
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const commitEdit = useCallback(() => {
    setIsEditing(false)
    const parsed = parseInt(draft, 10)
    if (isNaN(parsed)) return
    const clamped = Math.min(max, Math.max(min, parsed))
    onChange(clamped)
  }, [draft, min, max, onChange])

  const startEditing = useCallback(() => {
    if (!editable) return
    setDraft(String(value))
    setIsEditing(true)
    // Focus + select after state update
    requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    })
  }, [editable, value])

  return (
    <input
      ref={inputRef}
      type='text'
      inputMode={editable ? 'numeric' : undefined}
      readOnly={!isEditing}
      className={`FormStepper_tabular FormStepper_value${isEditing ? ' FormStepper_value--editing' : ''}${editable ? ' FormStepper_value--editable' : ''}`}
      value={isEditing ? draft : (displayValue ?? String(value))}
      onChange={isEditing ? (e) => setDraft(e.target.value.replace(/[^\d]/g, '')) : undefined}
      onFocus={startEditing}
      onBlur={isEditing ? commitEdit : undefined}
      onKeyDown={isEditing ? (e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commitEdit()
        }
        if (e.key === 'Escape') {
          setIsEditing(false)
          inputRef.current?.blur()
        }
      } : undefined}
      tabIndex={editable ? 0 : -1}
    />
  )
}

/** Standalone stepper (no Formik integration) */
export const Stepper = ({
  value,
  onChange,
  onIncrement,
  onDecrement,
  min = 0,
  max = Infinity,
  step = 1,
  incrementDisabled,
  decrementDisabled,
  displayValue,
  editable = true,
  ...stackProps
}: StepperProps) => {
  const handleDecrement = useCallback(() => {
    if (onDecrement) {
      onDecrement()
      return
    }
    const next = value - step
    if (next < min) {
      onChange(min)
    } else {
      onChange(next)
    }
  }, [value, step, min, onChange, onDecrement])

  const handleIncrement = useCallback(() => {
    if (onIncrement) {
      onIncrement()
      return
    }
    const next = value + step
    if (next > max) {
      onChange(max)
    } else {
      onChange(next)
    }
  }, [value, step, max, onChange, onIncrement])

  return (
    <XStack alignItems='center' gap='$3' {...stackProps}>
      <StepperButton
        onPress={handleDecrement}
        disabled={decrementDisabled ?? value <= min}
        icon='subtract'
      />

      <StepperValue
        value={value}
        displayValue={displayValue}
        editable={editable}
        onChange={onChange}
        min={min}
        max={max}
      />

      <StepperButton
        onPress={handleIncrement}
        disabled={incrementDisabled ?? value >= max}
        icon='add'
      />
    </XStack>
  )
}

/** Formik-integrated stepper */
export const FormStepper = ({
  min = 0,
  max = Infinity,
  step = 1,
  displayValue,
  onChange,
  ...stackProps
}: Omit<StepperProps, 'value'> & { onChange?: (value: number) => void } & Omit<XStackProps, 'children'>) => {
  const formFieldContext = useContext(FormFieldContext)
  const [field, _meta, helpers] = useField<number>(formFieldContext.name)

  const handleChange = useCallback(
    (value: number) => {
      helpers.setTouched(true)
      helpers.setValue(value)
      onChange?.(value)
    },
    [helpers, onChange]
  )

  return (
    <Stepper
      value={field.value ?? 0}
      onChange={handleChange}
      min={min}
      max={max}
      step={step}
      displayValue={displayValue}
      {...stackProps}
    />
  )
}
